import { api as generatedApi } from 'gen/rtk-openapi'
import { getNetWeight } from 'utils/data-manipulation/common'
import Dinero from 'dinero.js'
import { roundTo } from 'utils/data-manipulation/valueFormatter'
import { ROUNDING_PRECISIONS } from 'utils/constants'

const TAG_TYPES = {
  inboundTickets: 'InboundTickets',
  inboundTicketNotes: 'InboundTicketNotes',
  finishedGoods: 'FinishedGoods',
  packingList: 'PackingList',
  priceSheets: 'PriceSheets',
  inventoryCommodity: 'InventoryCommodity',
  purchaseOrders: 'PurchaseOrders',
  inventoryTransfers: 'InventoryTransfers',
  bills: 'Bills',
  queuedPayments: 'QueuedPayments',
}

const PRECISION = 4

export const api = generatedApi.enhanceEndpoints({
  addTagTypes: [TAG_TYPES.inboundTickets, TAG_TYPES.inventoryTransfers, TAG_TYPES.inboundTicketNotes],
  endpoints: {
    getInboundTickets: {
      providesTags: result =>
        result && result.Items
          ? [
              ...result.Items.flatMap(inboundTicket => {
                const tags = [{ type: TAG_TYPES.inboundTickets, id: inboundTicket.id }]
                const purchaseOrderId = inboundTicket?.transportationInfo?.purchaseOrderId
                if (purchaseOrderId) {
                  tags.push({
                    type: TAG_TYPES.inboundTickets,
                    id: purchaseOrderId,
                  })
                }
                return tags
              }),
              { type: TAG_TYPES.inboundTickets, id: 'LIST' },
            ]
          : // error occurred, refetch when `{ type: 'InboundTickets', id: 'LIST' }` is invalidated
            [{ type: TAG_TYPES.inboundTickets, id: 'LIST' }],
    },
    getInboundTicketsByIds: {}, // POST
    getInboundTicket: {
      providesTags: (result, error, { ticketId }) => {
        const tags = [
          {
            type: TAG_TYPES.inboundTickets,
            id: ticketId,
          },
        ]
        const purchaseOrderId = result?.transportationInfo?.purchaseOrderId
        if (purchaseOrderId) {
          tags.push({ type: TAG_TYPES.inboundTickets, id: purchaseOrderId })
        }
        const billId = result?.billId
        if (billId) {
          tags.push({ type: TAG_TYPES.inboundTickets, id: billId })
        }
        return tags
      },
    },
    createInboundTicket: {
      invalidatesTags: [
        { type: TAG_TYPES.inboundTickets, id: 'LIST' },
        { type: TAG_TYPES.bills, id: 'LIST' },
        { type: TAG_TYPES.finishedGoods },
        { type: TAG_TYPES.packingList },
        { type: TAG_TYPES.priceSheets },
        { type: TAG_TYPES.inventoryCommodity },
        { type: TAG_TYPES.purchaseOrders },
      ],
    },
    updateInboundTicket: {
      invalidatesTags: (result, error, { ticketId, billId }) => [
        { type: TAG_TYPES.inboundTickets, id: ticketId },
        { type: TAG_TYPES.inboundTickets, id: 'LIST' },
        { type: TAG_TYPES.bills, id: 'LIST' },
        { type: TAG_TYPES.bills, id: billId },
        { type: TAG_TYPES.finishedGoods },
        { type: TAG_TYPES.packingList },
        { type: TAG_TYPES.priceSheets },
        { type: TAG_TYPES.inventoryCommodity },
        { type: TAG_TYPES.purchaseOrders },
        { type: TAG_TYPES.queuedPayments, id: 'LIST' },
      ],
    },
    voidInboundTicket: {
      invalidatesTags: (result, error, { ticketId }) => [
        { type: TAG_TYPES.inboundTickets, id: 'LIST' },
        { type: TAG_TYPES.bills, id: 'LIST' },
        { type: TAG_TYPES.inboundTickets, id: ticketId },
        { type: TAG_TYPES.finishedGoods },
        { type: TAG_TYPES.packingList },
        { type: TAG_TYPES.priceSheets },
        { type: TAG_TYPES.inventoryCommodity },
      ],
    },
    mergeInboundTickets: {
      invalidatesTags: (result, error, { id, billId }) => [
        { type: TAG_TYPES.inboundTickets, id: 'LIST' },
        { type: TAG_TYPES.inboundTickets, id },
        { type: TAG_TYPES.bills, id: 'LIST' },
        { type: TAG_TYPES.bills, id: billId },
        { type: TAG_TYPES.finishedGoods },
        { type: TAG_TYPES.packingList },
        { type: TAG_TYPES.priceSheets },
        { type: TAG_TYPES.inventoryCommodity },
      ],
    },
    getTransfers: {
      providesTags: result =>
        result && result.Items
          ? // successful query
            [
              ...result.Items.map(transfer => ({
                type: TAG_TYPES.inventoryTransfers,
                id: transfer.id,
              })),
              { type: TAG_TYPES.inventoryTransfers, id: 'LIST' },
            ]
          : // error occurred, refetch when `{ type: 'InventoryTransfers', id: 'LIST' }` is invalidated
            [{ type: TAG_TYPES.inventoryTransfers, id: 'LIST' }],
    },
    getTransfer: {
      providesTags: (result, error, { transferId }) => [{ type: TAG_TYPES.inventoryTransfers, id: transferId }],
    },
    getInboundTicketNotes: {
      providesTags: result => {
        if (!result) return []
        return [
          ...result.map(note => ({
            type: TAG_TYPES.inboundTicketNotes,
            id: note.id,
          })),
          { type: TAG_TYPES.inboundTicketNotes, id: 'LIST' },
        ]
      },
    },
    deleteInboundTicketNote: {
      invalidatesTags: (_result, _error, { ibtNoteId }) => [
        { type: TAG_TYPES.inboundTicketNotes, id: 'LIST' },
        { type: TAG_TYPES.inboundTicketNotes, id: ibtNoteId },
        { type: TAG_TYPES.inboundTickets, id: 'LIST' },
      ],
    },
    createInboundTicketNote: {
      invalidatesTags: (_result, _error) => [
        { type: TAG_TYPES.inboundTicketNotes, id: 'LIST' },
        { type: TAG_TYPES.inboundTickets, id: 'LIST' },
      ],
    },
    updateInboundTicketNotes: {
      invalidatesTags: (result, _error) => [
        { type: TAG_TYPES.inboundTicketNotes, id: 'LIST' },
        { type: TAG_TYPES.inboundTickets, id: 'LIST' },
        ...result.map(note => ({ type: TAG_TYPES.inboundTicketNotes, id: note.id })),
      ],
    },
  },
})
export const useGetTransferLazyQuery = api.endpoints.getTransfer.useLazyQuery
export const {
  useGetInboundTicketsQuery,
  useGetInboundTicketsByIdsMutation,
  useGetInboundTicketQuery,
  useCreateInboundTicketMutation,
  useUpdateInboundTicketMutation,
  useVoidInboundTicketMutation,
  useMergeInboundTicketsMutation,
  useGetTransfersQuery,
  useGetTransferQuery,
  useGetInboundTicketNotesQuery,
  useDeleteInboundTicketNoteMutation,
  useCreateInboundTicketNoteMutation,
  useUpdateInboundTicketNotesMutation,
} = api

export const getMaterialRowWithNetFields = ({
  gross,
  tare,
  price,
  deductions,
  netPrice,
  um,
  netValue,
  ...restFields
}) => {
  const grossFormatted = Number(gross) ? Number(gross) : 0
  const tareFormatted = tare === null || tare === '' || tare === undefined ? null : Number(tare) || 0
  const priceFormatted = Number(price) ? Number(price) : 0
  const netRow = {
    ...restFields,
    gross: grossFormatted,
    tare: tareFormatted,
    price: priceFormatted,
    deductions,
    netWeight: getNetWeight({ gross, tare, deductions }),
    netPrice:
      (netPrice &&
        Dinero({
          ...netPrice,
          amount: parseInt(netPrice?.amount || 0, 10),
        }).toUnit()) ||
      Number(price),
    netValue:
      (netValue?.amount &&
        Dinero({
          ...netValue,
          amount: parseInt(netValue?.amount || 0, 10),
        }).toUnit()) ||
      Number(netValue || 0),
    um,
  }
  return netRow
}

export const transformMaterialToRow = ({
  material: {
    weightAndPrice: {
      gross,
      tare,
      deductions = [],
      netPrice,
      netValue,
      um,
      customerPreferredUm,
      customerPreferredPrice,
      isManualGross,
      isManualTare,
    },
    ...restFields
  },
}) => {
  const safeAmount = price => ({
    ...price,
    amount: price?.amount !== null ? price?.amount : 0,
  })

  const netPriceSafe = safeAmount(netPrice)
  const customerPreferredPriceSafe = safeAmount(customerPreferredPrice)
  const netValueSafe = safeAmount(netValue)

  const priceFormatted = Dinero({ ...netPriceSafe }).toUnit()
  const customerPreferredPriceFormatted = Dinero({ ...customerPreferredPriceSafe }).toUnit()
  const netValueFormatted = Dinero({ ...netValueSafe }).toUnit()

  const { status, materialType = {} } = restFields || {}
  const { grossImages, tareImages } = status || {}

  return {
    ...getMaterialRowWithNetFields({
      ...restFields,
      gross: Number(gross?.amount || 0),
      tare: tare === null ? null : Number(tare?.amount),
      deductions,
      um: um?.toLowerCase(),
      price: priceFormatted,
      netPrice: netPriceSafe,
      netValue: netValueFormatted,
      customerPreferredUm: customerPreferredUm?.toLowerCase(),
      customerPreferredPrice: customerPreferredPriceFormatted,
      isManualGross,
      isManualTare,
      scaleButtonClicked: false,
      tareWeightRequired: materialType?.tareWeightRequired,
      grossImages,
      tareImages,
    }),
  }
}

export const reverseTransformMaterialRow = ({
  id,
  rowIndex,
  materialTypeId,
  orderEntryId,
  code,
  gross,
  tare,
  deductions,
  netWeight,
  um,
  netPrice,
  netValue,
  grossImages,
  tareImages,
  customerPreferredUm,
  customerPreferredPrice,
  isManualGross,
  isManualTare,
  tareWeightRequired,
  axleWeights,
  finishedGoodItemCount,
  finishedGood,
}) => ({
  id,
  materialTypeId,
  orderEntryId,
  code,
  weightAndPrice: {
    customerPreferredUm: customerPreferredUm?.toLowerCase(),
    customerPreferredPrice: {
      amount: roundTo(customerPreferredPrice * 10 ** PRECISION),
      precision: PRECISION,
    },
    gross: { amount: Number(gross) },
    tare: tare === null ? null : { amount: Number(tare) },
    deductions,
    netWeight: { amount: Number(netWeight) },
    um: um?.toLowerCase(),
    netPrice: { amount: netPrice * 10 ** PRECISION, precision: PRECISION },

    // Make sure row totals are rounded up to 2 decimal places, to match the behaviour on ticket creation.
    // Failure to do so will result in mismatched values between BE and FE (see GREEN-8695)
    netValue: { amount: roundTo(Number(netValue), ROUNDING_PRECISIONS.COST) },
    isManualGross,
    isManualTare,
  },
  grossImages,
  tareImages,
  rowIndex,
  tareWeightRequired,
  axleWeights,
  ...(finishedGood?.id && {
    finishedGood: {
      ...finishedGood,
      itemCount: Number(finishedGoodItemCount) || 0,
    },
  }),
})

export const transformInboundTicket = ({
  inboundTicketData: {
    truckWeight,
    loadWeight,
    materials = [],
    netWeight,
    netPrice,
    netValue,
    effectiveDate,
    transportationInfo,
    status,
    carrierId,
    notes = [],
    scrapVehicles = [],
    freightCost,
    feeOrPromotion = [],
    ...restFields
  },
  carrierData,
}) => {
  const mats = materials.map(material => transformMaterialToRow({ material }))
  const freightChargeback = freightCost?.chargeBack || 0

  return {
    truckWeight: {
      gross: Number(truckWeight?.gross?.amount || 0),
      tare: Number(truckWeight?.tare?.amount || 0),
      net: Number(truckWeight?.net?.amount || 0),
      isManualGross: truckWeight?.isManualGross,
      isManualTare: truckWeight?.isManualTare,
    },
    loadWeight: {
      gross: Number(loadWeight?.gross || 0),
      tare: Number(loadWeight?.tare || 0),
      deductions: Number(loadWeight?.deductions || 0),
      net: Number(loadWeight?.net || 0),
    },
    materials: mats,
    netWeight: Number(netWeight || 0),
    netPrice: Number(Dinero({ ...netPrice }).toUnit()),
    netValue: Number(Dinero({ ...netValue }).toUnit()) + freightChargeback,
    effectiveDate: new Date(effectiveDate),
    notes,
    transportationInfo: {
      ...transportationInfo,
      carrierLookup: carrierId === carrierData?.id ? carrierData : null,
      billOfLading: transportationInfo.billOfLading || '',
      carrierName: transportationInfo.carrierName || '',
      carrierNumber: transportationInfo.carrierNumber || '',
      containerNumber: transportationInfo.containerNumber || '',
      driverName: transportationInfo.driverName || '',
      trailerNumber: transportationInfo.trailerNumber || '',
      purchaseOrderNumber: transportationInfo.purchaseOrderNumber || '',
      purchaseOrderId: transportationInfo.purchaseOrderId || '',
      isPickup: String(transportationInfo.isPickup || 'false'),
      transportationType: transportationInfo.transportationType || '',
    },
    status,
    scrapVehicles,
    percentage: 0,
    freightCost: {
      cost: freightCost?.cost || 0,
      chargeBack: freightChargeback,
      userSet: freightCost?.userSet || false,
      userChargebackSet: freightCost?.userChargebackSet || false,
      id: freightCost?.id || '',
    },
    ibtFeesAndPromotions: feeOrPromotion,
    ...restFields,
  }
}
